import { AbsoluteCenter, Spinner } from '@chakra-ui/react';
import { useCallback, useEffect } from 'react';
import { InView } from 'react-intersection-observer';

import { useSocketContext } from 'app/socket';
import { Box, Divider } from 'shared';
import Spacer from 'shared_DEPRECATED/components/Spacer';

import ChatMessages from 'features/chat/components/ChatMessages/ChatMessages';
import { ChatMessagesContainer } from 'features/chat/components/ChatMessages/ChatMessagesContainer';
import { ScheduledChatMessages } from 'features/chat/components/ChatMessagesScheduling';
import ChatReconnectionIndicator from 'features/chat/components/ChatReconnectionIndicator';
import { useChatContext } from 'features/chat/context/chat';
import { useChatMessageSchedulingContext } from 'features/chat/context/chatMessageScheduling';
import { useChatMessagesContext } from 'features/chat/context/chatMessages';

import { ScrollContainer } from '../ScrollContainer';
import { useChatMessagesReadAllMutation } from '../hooks/mutation/useChatMessagesReadAll';
import { useToggleChatReadStatusMutation } from '../hooks/mutation/useToggleChatReadStatus';
import { ChatForm } from './ChatForm';
import { ChatMessagesEmptyState } from './ChatMessages/EmptyState';

export const ChatBody = () => {
  const { isSocketReady } = useSocketContext();
  const { mutateAsync: toggleChatReadStatus } =
    useToggleChatReadStatusMutation();
  const {
    messages,
    isFetching,
    hasFailedMessages,
    retrySendingMessage,
    loadReadMessages,
    markAsRead,
    sendReaction,
    chatRef,
    isLoading,
  } = useChatMessagesContext();

  const { unreadMessagesCount, isChatMarkedAsUnread } = useChatContext();
  const isChatEmpty = messages.length === 0;
  const skipTrack = !isSocketReady || isChatEmpty;
  const readAllMutation = useChatMessagesReadAllMutation();
  const { scheduledMessages, isScheduledListVisible, hideScheduledMessages } =
    useChatMessageSchedulingContext();

  const handleScrollToBottom = useCallback(() => {
    if (unreadMessagesCount > 0) {
      readAllMutation.mutateAsync();
    }
  }, [readAllMutation, unreadMessagesCount]);

  useEffect(() => {
    if (isChatMarkedAsUnread) {
      toggleChatReadStatus();
    }
  }, [isChatMarkedAsUnread, toggleChatReadStatus]);

  if (isLoading) {
    return (
      <AbsoluteCenter axis="both" w="full">
        <Spinner size="md" />
      </AbsoluteCenter>
    );
  }

  return (
    <>
      {!isSocketReady && <ChatReconnectionIndicator />}
      <ChatMessagesContainer
        ref={chatRef}
        showLoader={isFetching}
        hasFailedMessages={hasFailedMessages}
      >
        {isScheduledListVisible ? (
          <ScheduledChatMessages
            onClose={hideScheduledMessages}
            messages={scheduledMessages}
          />
        ) : (
          <ChatMessagesEmptyState hasItems={!isChatEmpty}>
            <ScrollContainer
              messages={messages}
              onScrollToBottom={handleScrollToBottom}
            >
              <Box border="1px solid transparent">
                <InView skip={skipTrack} onChange={loadReadMessages} />
              </Box>
              <ChatMessages
                messages={messages}
                markAsRead={markAsRead}
                sendReaction={sendReaction}
                retrySendingMessage={retrySendingMessage}
              />
            </ScrollContainer>
          </ChatMessagesEmptyState>
        )}
      </ChatMessagesContainer>
      <Divider />
      <Spacer size="sm" />
      <ChatForm disabled={!isSocketReady} />
    </>
  );
};
